import { Helmet } from 'react-helmet-async'
import ShimmerButton from '../components/ui/ShimmerButton'
import { BentoGrid, BentoGridItem } from '../components/ui/BentoGrid'
import SpotlightCard from '../components/ui/SpotlightCard'
import LogoMarquee from '../components/ui/LogoMarquee'
import GrantaEarthLogo from '../components/ui/GrantaEarthLogo'
import { Link } from 'react-router-dom'

const SectionTitle = ({title, subtitle}) => (
  <div className="text-center mb-10">
    <h2 className="text-3xl md:text-4xl font-bold">{title}</h2>
    {subtitle && <p className="mt-3 text-gray-600 max-w-2xl mx-auto">{subtitle}</p>}
  </div>
);

const Home = () => {
  return (
    <>
      <Helmet>
        <title>GrantaEarth Co. | Mining, Energy, Commodities</title>
        <meta name="description" content="Zimbabwe based operator in chrome mining, bulk fuel trading, and commodities. Reliable supply for global partners." />
      </Helmet>

      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-gradient-to-b from-off-white to-white">
        <div className="absolute inset-0 bg-[url('/globe-grid.svg')] bg-center bg-[length:1200px_1200px] opacity-20 pointer-events-none" />
        <div className="container-custom section-padding text-center">
          <div className="mb-8">
            <GrantaEarthLogo size="xxl" className="mx-auto mb-6 drop-shadow-lg" />
          </div>
          
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight bg-gradient-to-r from-deep-green to-copper bg-clip-text text-transparent">
            Turning African resources into reliable supply for global markets
          </h1>
          <p className="mt-6 text-lg text-gray-700 max-w-3xl mx-auto">
            Zimbabwe based operator delivering consistent results across mining, energy, and commodity trade. We combine local expertise with international standards to create sustainable partnerships that drive growth across African supply chains.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <ShimmerButton>Contact Trade Desk</ShimmerButton>
            </Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE - Using BentoGrid */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <SectionTitle
            title="Why Choose GrantaEarth"
            subtitle="Proven capabilities and strategic advantages that make us your ideal African resources partner."
          />
          <BentoGrid>
            <BentoGridItem 
              title="Regional Reach" 
              description="Registered operations in Zimbabwe with access to strategic mineral regions and established supply chains."
              icon="🌍"
            />
            <BentoGridItem 
              title="Chrome Mining" 
              description="Active claims and tributary agreements in Lalapanzi, Zvishavane, and Mutorashanga regions."
              icon="⛏️"
            />
            <BentoGridItem 
              title="Energy Trading" 
              description="Bulk fuel distribution for transport, agriculture, and industry across the Southern African region."
              icon="⛽"
            />
            <BentoGridItem 
              title="Commodities Desk" 
              description="Exports of minerals and imports of grain, fuel, fertilisers, and bitumen with reliable logistics."
              icon="📦"
            />
            <BentoGridItem 
              title="Ethical Standards" 
              description="Transparent trade practices with strong compliance frameworks and environmental stewardship."
              icon="✅"
            />
            <BentoGridItem 
              title="Risk Management" 
              description="Structured deals that reduce currency exposure and supply volatility for all stakeholders."
              icon="🛡️"
            />
          </BentoGrid>
        </div>
      </section>

      {/* BUSINESS UNITS - Using SpotlightCard */}
      <section className="bg-gradient-to-b from-white to-off-white section-padding">
        <div className="container-custom">
          <SectionTitle title="Business Units" />
          <div className="grid gap-8 md:grid-cols-3">
            <SpotlightCard
              title="Mining"
              description="Chrome focused operations with disciplined safety protocols and reliable offtake agreements."
              image="https://images.unsplash.com/photo-1581093588401-16a89b127aeb?q=80&w=1400&auto=format&fit=crop"
              href="/units/mining"
            />
            <SpotlightCard
              title="Energy"
              description="Bulk fuel trading with dependable logistics and contract backed delivery across the region."
              image="https://images.unsplash.com/photo-1502303756787-90e6b8dba79f?q=80&w=1400&auto=format&fit=crop"
              href="/units/energy"
            />
            <SpotlightCard
              title="Commodities"
              description="Trade desk for minerals out of Zimbabwe and essential inputs into Zimbabwe markets."
              image="https://images.unsplash.com/photo-1549388604-817d15aa0110?q=80&w=1400&auto=format&fit=crop"
              href="/units/commodities"
            />
          </div>
        </div>
      </section>

      {/* MINING OPERATIONS SHOWCASE */}
      <section className="bg-gradient-to-b from-off-white to-white section-padding">
        <div className="container-custom">
          <SectionTitle 
            title="Chrome Mining Operations" 
            subtitle="Active mining sites across Zimbabwe producing high-quality chrome ore for global markets."
          />
          
          <div className="grid gap-8 md:grid-cols-2 lg:gap-12">
            <div className="group">
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <img 
                  src="/images/mining/chrome-mining-site.png" 
                  alt="Chrome mining site with trucks and equipment" 
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-white text-lg font-semibold mb-1">Active Mining Sites</h3>
                  <p className="text-white/90 text-sm">
                    Operational sites in Lalapanzi, Zvishavane, and Mutorashanga
                  </p>
                </div>
              </div>
            </div>

            <div className="group">
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <img 
                  src="/images/mining/chrome-ore-detail.png" 
                  alt="High-quality chrome ore stockpile" 
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-white text-lg font-semibold mb-1">Quality Chrome Ore</h3>
                  <p className="text-white/90 text-sm">
                    Consistent grade and sizing for international export
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link to="/mining">
              <ShimmerButton>Explore Mining Operations</ShimmerButton>
            </Link>
          </div>
        </div>
      </section>

      {/* PARTNERSHIPS - Using LogoMarquee */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <SectionTitle 
            title="Trusted by partners across Africa and beyond" 
            subtitle="Building lasting relationships with leading companies in mining, energy, and commodities sectors."
          />
          <LogoMarquee 
            speed="medium"
            pauseOnHover={true}
            logos={[
              { name: "Mining Corp", url: "#" },
              { name: "Energy Solutions", url: "#" },
              { name: "Global Trade", url: "#" },
              { name: "African Partners", url: "#" },
              { name: "Resource Group", url: "#" },
              { name: "Trade Alliance", url: "#" },
            ]}
          />
        </div>
      </section>

      {/* CONTACT */}
      <section className="bg-gradient-to-r from-off-white to-white section-padding">
        <div className="container-custom grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold">Contact</h2>
            <p className="mt-3 text-gray-600">Send a message to the trade desk. We respond quickly to serious investment or supply requests.</p>
            <form className="mt-8 space-y-4">
              <label className="block">
                <span className="text-sm font-medium text-gray-700">Name</span>
                <input className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-deep-green" type="text" name="name" />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">Email</span>
                <input className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-deep-green" type="email" name="email" />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">Company</span>
                <input className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-deep-green" type="text" name="company" />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">Message</span>
                <textarea rows="4" className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-deep-green" name="message" />
              </label>
              <ShimmerButton type="submit">Send Message</ShimmerButton>
            </form>
          </div>

          <div className="relative rounded-2xl border bg-white shadow min-h-[320px]">
            <div className="absolute inset-0 bg-[url('/globe-grid.svg')] bg-center bg-contain opacity-20" />
            <div className="p-6 relative">
              <GrantaEarthLogo size="lg" className="mb-4" />
              <p className="mt-2 text-gray-600">Zimbabwe registered. Operating across Lalapanzi, Zvishavane, and Mutorashanga.</p>
              <div className="mt-4 space-y-1 text-gray-700">
                <p>Email: info@grantaearth.com</p>
                <p>Phone: +263719263479</p>
                <p>Working hours: Mon to Fri</p>
                <p>8Am to 5pm</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Home
